// Canonical page-id -> localized full path map. Single source of truth for
// every internal link, the language switcher, hreflang alternates and the
// sitemap. Paths include the locale prefix and a leading slash.
//
// Slugs for pages that exist on the live misupport.dk site are taken 1:1 from
// its sitemaps so SEO is preserved. English/German entries are filled in as
// each page is localized (phased rollout); where an en/de slug is not yet
// defined, pathFor() falls back to the Danish path so links never 404.

import { type Locale } from "./config";

export type PageId =
  // top level
  | "home"
  | "kontakt"
  | "samarbejdspartnere"
  | "modenmisupporter"
  | "artikler"
  // services
  | "ydelser"
  | "projektledelse"
  | "byggeledelse"
  | "bygherreraadgivning"
  | "tidsplanlaegning"
  | "projektoekonomi"
  | "projektassistance"
  | "shutdown"
  | "hse"
  | "sitecompliance"
  // landing pages
  | "gratissikkerhedstjek"
  // industries
  | "brancher"
  | "datacentre"
  | "pharma"
  | "industri"
  | "energi"
  | "hospitaler"
  | "kontor"
  | "transport"
  // about
  | "omos"
  | "viden"
  | "esg"
  | "karriere"
  | "arbejdsplads"
  // legal
  | "cookiepolitik"
  | "persondata";

/** en/de optional during rollout; pathFor falls back to the Danish path. */
type SlugEntry = { da: string } & Partial<Record<Locale, string>>;

// IMPORTANT: only add en/de entries for pages whose localized ROUTE actually
// exists. pathFor() falls back to the Danish path when en/de is missing, so a
// not-yet-built page links to its working Danish route instead of a 404. The
// real live en/de slugs to use when building each page are listed in
// PLANNED_SLUGS below for the rollout.
export const slugMap: Record<PageId, SlugEntry> = {
  // ---- pilot pages: localized routes exist on all three languages ----
  home: { da: "/", en: "/en", de: "/de" },
  byggeledelse: {
    da: "/byggeledelse",
    en: "/en/construction-management",
    de: "/de/baumanagement",
  },
  datacentre: {
    da: "/brancher/datacentre",
    en: "/en/industries/data-centers",
    de: "/de/branchen/rechenzentren",
  },

  // ---- live-matched pages: slugs 1:1 from the old WordPress site (SEO) ----
  kontakt: { da: "/kontakt-mi-support", en: "/en/contact-mi-support", de: "/de/kontakt-mi-support" },
  samarbejdspartnere: { da: "/samarbejdspartnere", en: "/en/business-partners", de: "/de/geschaftspartner" },
  modenmisupporter: { da: "/mod-en-mi-supporter", en: "/en/meet-a-mi-supporter", de: "/de/gegenuber-einem-mitstreiter" },
  artikler: { da: "/artikler", en: "/en/articles", de: "/de/artikel" },
  projektledelse: { da: "/projektledelse", en: "/en/project-management", de: "/de/projektleitung" },
  hse: { da: "/hse-ledelse", en: "/en/hse-management", de: "/de/hse-management" },
  omos: { da: "/om-os", en: "/en/about-us", de: "/de/uber-uns" },
  arbejdsplads: { da: "/mi-support-som-arbejdsplads", en: "/en/mi-support-as-a-workplace", de: "/de/mi-unterstutzung-als-arbeitsplatz" },

  // ---- legal pages (recreated on the old URLs) ----
  cookiepolitik: { da: "/cookie-og-privatlivspolitik", en: "/en/cookie-and-privacy-policy", de: "/de/cookie-und-datenschutzrichtlinie" },
  persondata: { da: "/persondatapolitik", en: "/en/datapolicy", de: "/de/datenschutzbestimmungen" },

  // ---- clone-only pages, now fully localized (slugs are our own, no old URL) ----
  ydelser: { da: "/ydelser", en: "/en/services", de: "/de/leistungen" },
  bygherreraadgivning: { da: "/bygherreraadgivning", en: "/en/client-consultancy", de: "/de/bauherrenberatung" },
  tidsplanlaegning: { da: "/tidsplanlaegning", en: "/en/scheduling", de: "/de/terminplanung" },
  projektoekonomi: { da: "/projektoekonomi", en: "/en/project-economics", de: "/de/projektoekonomie" },
  projektassistance: { da: "/projektassistance", en: "/en/project-assistance", de: "/de/projektassistenz" },
  shutdown: { da: "/shutdown-planlaegning", en: "/en/shutdown-planning", de: "/de/shutdown-planung" },
  sitecompliance: { da: "/sitecompliance" },
  gratissikkerhedstjek: { da: "/gratis-sikkerhedstjek-landing" },
  viden: { da: "/viden" },
  brancher: { da: "/brancher", en: "/en/industries", de: "/de/branchen" },
  pharma: { da: "/brancher/pharma-life-science", en: "/en/industries/pharma-life-science", de: "/de/branchen/pharma-life-science" },
  industri: { da: "/brancher/industri-produktion", en: "/en/industries/industry-manufacturing", de: "/de/branchen/industrie-produktion" },
  energi: { da: "/brancher/energi-infrastruktur", en: "/en/industries/energy-infrastructure", de: "/de/branchen/energie-infrastruktur" },
  hospitaler: { da: "/brancher/hospitaler-sundhed", en: "/en/industries/hospitals-healthcare", de: "/de/branchen/krankenhaeuser-gesundheit" },
  kontor: { da: "/brancher/kontor-hovedsaeder", en: "/en/industries/offices-headquarters", de: "/de/branchen/bueros-hauptsitze" },
  transport: { da: "/brancher/transport-logistik", en: "/en/industries/transport-logistics", de: "/de/branchen/transport-logistik" },
  esg: { da: "/esg", en: "/en/esg", de: "/de/esg" },
  karriere: { da: "/karriere", en: "/en/careers", de: "/de/karriere" },
};

/** Localized full path for a page; falls back to Danish when not yet translated. */
export function pathFor(id: PageId, locale: Locale): string {
  return slugMap[id][locale] ?? slugMap[id].da;
}

/** True when a localized route actually exists for this page+locale. */
export function hasLocalizedPath(id: PageId, locale: Locale): boolean {
  return slugMap[id][locale] != null;
}

// Reverse lookup: pathname -> page-id. Built from every defined locale path so
// the language switcher can identify the current page regardless of language.
const reverse = new Map<string, PageId>();
for (const key of Object.keys(slugMap) as PageId[]) {
  const entry = slugMap[key];
  for (const loc of ["da", "en", "de"] as const) {
    const p = entry[loc];
    if (p) reverse.set(p, key);
  }
}

export function pageIdFromPath(pathname: string): PageId | undefined {
  return reverse.get(pathname);
}
